import { TextSelection } from '@tiptap/pm/state';
import {
  insertResourceRowContinuationLine,
  resolveExitPlacementAfterBlock,
  runExitContainerOnEnter,
  shouldExitContainerOnEnter,
  splitContainerLineOnEnter,
} from './block_exit_on_enter.js';
import {
  findResourceRowDepth,
  findResourceTableDepth,
  findSubtitleChildIndex,
  isResourceRowGapParagraph,
  lastTitleChildIndex,
  mergeBackspaceFromParagraphBelow,
  paragraphIsEffectivelyEmpty,
  paragraphRowRole,
  paragraphTextEndPos,
  resourceRowHasSubtitle,
  rowChildStartPos,
  setSelectionAtParagraphContentEnd,
} from './resource_row_paragraph.js';

function findAncestorDepth($from, nodeName) {
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === nodeName) return d;
  }
  return -1;
}

function blockquoteChildStartPos(blockquotePos, blockquoteNode, childIndex) {
  let pos = blockquotePos + 1;
  for (let i = 0; i < childIndex; i++) {
    pos += blockquoteNode.child(i).nodeSize;
  }
  return pos;
}

function lastQuoteChildIndex(blockquoteNode) {
  let lastIndex = 0;
  blockquoteNode.forEach((child, _offset, index) => {
    if (child.attrs?.psQuoteRole !== 'attribution') lastIndex = index;
  });
  return lastIndex;
}

/**
 * Backspace at start of an extra empty line (gap / double-Enter staging): remove that line only,
 * move cursor to end of the previous line — do not unwrap the whole blockquote or row.
 */
export function backspaceFromExtraLineAtStart(editor, $from, config) {
  const { containerDepth, childStartPos, skipParagraph } = config;
  if ($from.depth !== containerDepth + 1) return false;
  if (!paragraphIsEffectivelyEmpty($from.parent)) return false;

  const container = $from.node(containerDepth);
  const containerPos = $from.before(containerDepth);
  const index = $from.index(containerDepth);
  if (index <= 0) return false;
  if (skipParagraph?.($from.parent)) return false;

  const prevIndex = index - 1;
  const prevPara = container.child(prevIndex);
  const prevStart = childStartPos(containerPos, container, prevIndex);
  const cursorPos = paragraphTextEndPos(prevStart, prevPara);

  const paraStart = $from.before($from.depth);
  const paraEnd = paraStart + $from.parent.nodeSize;

  return editor
    .chain()
    .command(({ tr, dispatch }) => {
      tr.delete(paraStart, paraEnd);
      const mapped = tr.mapping.map(cursorPos, -1);
      tr.setSelection(TextSelection.create(tr.doc, Math.max(1, mapped)));
      if (dispatch) dispatch(tr);
      return true;
    })
    .focus()
    .run();
}

/**
 * Backspace at start of attribution / subtitle: move to end of quote / title, remove secondary line.
 */
export function backspaceFromSecondaryLineStart(editor, $from, config) {
  const { containerDepth, getPrimaryChildIndex, childStartPos } = config;
  if ($from.depth !== containerDepth + 1) return false;

  const container = $from.node(containerDepth);
  const containerPos = $from.before(containerDepth);
  const primaryIndex = getPrimaryChildIndex(container);
  if (primaryIndex < 0) return false;

  const primaryPara = container.child(primaryIndex);
  const primaryStart = childStartPos(containerPos, container, primaryIndex);
  const cursorPos = paragraphTextEndPos(primaryStart, primaryPara);

  const paraStart = $from.before($from.depth);
  const paraEnd = paraStart + $from.parent.nodeSize;

  return editor
    .chain()
    .focus()
    .command(({ tr, dispatch }) => {
      tr.delete(paraStart, paraEnd);
      const mapped = tr.mapping.map(cursorPos, -1);
      tr.setSelection(TextSelection.create(tr.doc, Math.max(1, mapped)));
      if (dispatch) dispatch(tr);
      return true;
    })
    .run();
}

/**
 * Shared Enter rules: secondary line (attribution/subtitle) exits in one press;
 * primary line (quote/title) uses double-Enter; exit lands in/focuses paragraph after block.
 */
export function handlePullQuoteStyleEnter(editor, state, $from, config) {
  const {
    containerDepth,
    exitBlockDepth,
    isSecondaryParagraph,
    isGapParagraph,
    insertContinuationLine,
    hasSecondaryLine,
    onGapLineEnter,
  } = config;

  if ($from.depth !== containerDepth + 1) return false;

  const atParagraphEnd = $from.parentOffset === $from.parent.content.size;
  const exitPlacement = ($cursor, s) =>
    resolveExitPlacementAfterBlock(s, $cursor, exitBlockDepth($cursor));
  const exitOptions = { getExitContainerDepth: exitBlockDepth };

  if (isSecondaryParagraph($from.parent) && atParagraphEnd) {
    return runExitContainerOnEnter(editor, exitPlacement, exitOptions);
  }

  if (
    !isSecondaryParagraph($from.parent) &&
    atParagraphEnd &&
    !paragraphIsEffectivelyEmpty($from.parent) &&
    !isGapParagraph?.($from.parent)
  ) {
    if (shouldExitContainerOnEnter($from)) {
      return runExitContainerOnEnter(editor, exitPlacement, exitOptions);
    }
    if (insertContinuationLine) {
      return insertContinuationLine(editor, $from, {
        asGap: hasSecondaryLine?.($from),
      });
    }
    return splitContainerLineOnEnter(editor);
  }

  if (isGapParagraph?.($from.parent) && atParagraphEnd && onGapLineEnter) {
    return onGapLineEnter(editor, $from);
  }

  if (shouldExitContainerOnEnter($from)) {
    return runExitContainerOnEnter(editor, exitPlacement, exitOptions);
  }

  return false;
}

/** Blockquote Enter (quote + attribution). */
export function handleBlockquoteEnter(editor, state, $from, blockquoteDepth) {
  return handlePullQuoteStyleEnter(editor, state, $from, {
    containerDepth: blockquoteDepth,
    exitBlockDepth: ($cursor) => {
      const d = findAncestorDepth($cursor, 'blockquote');
      return d >= 0 ? d : blockquoteDepth;
    },
    isSecondaryParagraph: (p) => p.attrs?.psQuoteRole === 'attribution',
    insertContinuationLine: null,
    hasSecondaryLine: null,
  });
}

/** Resource row Enter (title + subtitle) — mirrors blockquote. */
export function handleResourceRowEnter(editor, state, $from) {
  const rowDepth = findResourceRowDepth($from);
  if (rowDepth < 0) return false;

  const rowNode = $from.node(rowDepth);
  const rowPos = $from.before(rowDepth);

  return handlePullQuoteStyleEnter(editor, state, $from, {
    containerDepth: rowDepth,
    exitBlockDepth: ($cursor) => {
      const tableDepth = findResourceTableDepth($cursor);
      return tableDepth >= 0 ? tableDepth : rowDepth;
    },
    isSecondaryParagraph: (p) => p.attrs?.psRowRole === 'subtitle',
    isGapParagraph: (p) => isResourceRowGapParagraph(p),
    insertContinuationLine: (ed, $pos, opts) =>
      insertResourceRowContinuationLine(ed, $pos, opts),
    hasSecondaryLine: () => resourceRowHasSubtitle(rowNode),
    onGapLineEnter: (ed) => {
      const subtitleIndex = findSubtitleChildIndex(rowNode);
      if (subtitleIndex < 0) return true;
      const subtitlePos = rowChildStartPos(rowPos, rowNode, subtitleIndex);
      return ed.chain().focus().setTextSelection(subtitlePos + 1).run();
    },
  });
}

export function backspaceBlockquoteAttributionStart(editor, $from, blockquoteDepth) {
  if ($from.parent.attrs?.psQuoteRole !== 'attribution') return false;
  return backspaceFromSecondaryLineStart(editor, $from, {
    containerDepth: blockquoteDepth,
    getPrimaryChildIndex: lastQuoteChildIndex,
    childStartPos: blockquoteChildStartPos,
  });
}

export function backspaceResourceRowSubtitleStart(editor, $from, rowDepth) {
  if (paragraphRowRole($from.parent) !== 'subtitle') return false;

  const rowNode = $from.node(rowDepth);
  const rowPos = $from.before(rowDepth);
  const indexInRow = $from.index(rowDepth);
  if (indexInRow <= 0) return false;

  const prevIndex = indexInRow - 1;
  const prevPara = rowNode.child(prevIndex);
  const prevStart = rowChildStartPos(rowPos, rowNode, prevIndex);

  if (paragraphIsEffectivelyEmpty($from.parent)) {
    const paraStart = $from.before($from.depth);
    const paraEnd = paraStart + $from.parent.nodeSize;

    return editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        tr.delete(paraStart, paraEnd);
        tr = setSelectionAtParagraphContentEnd(tr, tr.mapping.map(prevStart, -1));
        if (dispatch) dispatch(tr);
        return true;
      })
      .run();
  }

  if (paragraphIsEffectivelyEmpty(prevPara)) {
    const prevEnd = prevStart + prevPara.nodeSize;
    const cursorPos = $from.pos;
    const currentParaStart = $from.before($from.depth);

    return editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        tr.delete(prevStart, prevEnd);
        const mapped = tr.mapping.map(cursorPos, -1);
        const $mapped = tr.doc.resolve(Math.max(1, Math.min(mapped, tr.doc.content.size - 1)));
        if (findResourceRowDepth($mapped) >= 0) {
          try {
            tr.setSelection(TextSelection.create(tr.doc, mapped));
          } catch {
            tr.setSelection(TextSelection.near($mapped, -1));
          }
        } else {
          tr = setSelectionAtParagraphContentEnd(
            tr,
            tr.mapping.map(currentParaStart, -1)
          );
        }
        if (dispatch) dispatch(tr);
        return true;
      })
      .run();
  }

  return mergeBackspaceFromParagraphBelow(editor, $from, prevStart, prevPara);
}

export function backspaceResourceRowExtraLineAtStart(editor, $from, rowDepth) {
  const rowNode = $from.node(rowDepth);
  const rowPos = $from.before(rowDepth);
  return backspaceFromExtraLineAtStart(editor, $from, {
    containerDepth: rowDepth,
    skipParagraph: (p) => paragraphRowRole(p) === 'subtitle',
    childStartPos: (_containerPos, _container, childIndex) =>
      rowChildStartPos(rowPos, rowNode, childIndex),
  });
}

export function backspaceBlockquoteExtraLineAtStart(editor, $from, blockquoteDepth) {
  return backspaceFromExtraLineAtStart(editor, $from, {
    containerDepth: blockquoteDepth,
    skipParagraph: (p) => p.attrs?.psQuoteRole === 'attribution',
    childStartPos: blockquoteChildStartPos,
  });
}
